// The MATE BAR (`.context/alarms-redesign/spec-shared.md`, "Mate bar — PHASE
// 3"): the full-width command line above the segmented selector, on every screen
// of the Alarms page. Type what the boat should watch, press Enter, and the mate
// drafts an alarm you can confirm.
//
// It is the EXISTING Neo machinery (ADR 0161) in the page's own chrome — not a
// second assistant. The turn streams from `POST /v1/neo/chat/stream` with this
// boat scoped in, the SSE frames come through the shared reader
// (`lib/neoStream.ts`, moved verbatim out of components/NeoChat.tsx so both read
// one wire format), and every decision about what the frames MEAN lives in
// `lib/mateBar.ts`, unit-tested against synthetic sequences.
//
// Three rules this file exists to keep:
//
//   - **The mate never applies anything.** Drafts render as cards; the owner's
//     click on CONFIRM is the only thing that writes (MateProposalCard).
//   - **One turn, one line.** This is a command bar, not a chat. The latest
//     turn's text reply is a single muted line with the full text on its `title`;
//     no history is shown, and a reload clears the line. Only the conversation
//     id is kept, in this tab's sessionStorage (TOM-252), so a reload continues
//     the same conversation rather than leaving one summary per reload. The
//     reply arrives as Markdown, so `mateReply` flattens it to plain text — and
//     drops it entirely on a turn that drafted, where the card is the answer and
//     the text was only narrating it.
//   - **Never a fabricated draft.** When the turn fails, the bar says so in one
//     honest line — unless real drafts arrived with the failure, which ADR 0161
//     emits deliberately, in which case the cards are the answer.
//
// NO VOICE. The handoff draws a HOLD TO TALK button beside the input; nothing in
// this codebase captures speech, and a dead control is banned. Text only.

import { useEffect, useRef, useState, type FormEvent } from 'react';

import { useAuth } from '../../../auth';
import { demoLimitMessage } from '../../../lib/demo';
import { CheckCircleFillIcon } from '../../app/icons';
import { DASHBOARD_UPDATED_EVENT } from '../../NeoChat';
import {
  endMateTurn,
  foldMateEvent,
  mateBusyLabel,
  mateErrorLine,
  mateReceiptMessage,
  mateReply,
  removeMateProposal,
  startMateTurn,
  type MateReceipt,
  type MateTurnState,
} from '../../../lib/mateBar';
import { NEO_AGENTS_CAPABILITY } from '../../../lib/neoAgentProgress';
import {
  alarmChangeChip,
  parseAlarmChangeRequests,
  parseAlarmChanges,
  type NeoAlarmChange,
  type NeoAlarmChangeRequest,
} from '../../../lib/neoAlarmChange';
import {
  clearNeoConversationId,
  isNeoConversationGone,
  loadNeoConversationId,
  saveNeoConversationId,
} from '../../../lib/neoChatResume';
import { NEO_MEMORIES_UPDATED_EVENT, parseMemoryChanges } from '../../../lib/neoMemories';
import { NEO_BOARD_UPDATED_EVENT, parseBoardChanges } from '../../../lib/neoBoard';
import { parsePreferenceChanges } from '../../../lib/neoPreferences';
import { readNeoStream } from '../../../lib/neoStream';
import { NeoAlarmChangeCard } from '../../NeoAlarmChangeCard';
import { MateProposalCard, MateAlarmIcon } from './MateProposalCard';

const PLACEHOLDER =
  'Tell the boat — “warn me if battery 2 drops below 11.8 at anchor” · press Enter';

interface MateBarProps {
  readonly boatId: string;
  /** Fired the first time drafts arrive in a turn: the design routes a proposal
   * to ALL ALARMS, where the cards sit under the bar. */
  readonly onProposals: () => void;
  /** Fired after a confirmed create, so the page refetches its snapshot. The
   * card also broadcasts `ALARMS_UPDATED_EVENT`, which `useBoatAlerts` listens
   * for; this keeps the page's own refresh explicit rather than implied. */
  readonly onChange: () => void;
}

export function MateBar({ boatId, onProposals, onChange }: MateBarProps) {
  const auth = useAuth();
  const userId = auth.user?.id ?? null;
  // Whose and which boat's conversation the bar continues after a reload
  // (TOM-252). Null until the account is known: nothing is kept then.
  const resumeScope = userId === null ? null : { userId, boatId };
  const [draft, setDraft] = useState('');
  // The latest turn only — null before the first command.
  const [turn, setTurn] = useState<MateTurnState | null>(null);
  // What the owner typed for that turn; the proposal card quotes it back.
  const [command, setCommand] = useState('');
  const [sending, setSending] = useState(false);
  const [receipt, setReceipt] = useState<MateReceipt | null>(null);
  // Neo's own conversation thread, so a follow-up ("make that 11.6") lands in
  // context rather than starting cold. Kept across a reload for this boat.
  const [conversationId, setConversationId] = useState<string | null>(() =>
    loadNeoConversationId('mate', resumeScope)
  );
  const conversationIdRef = useRef<string | null>(null);
  conversationIdRef.current = conversationId;

  // Another boat (or account) on the same mounted bar: its own kept
  // conversation, or a new one. The read clears a kept conversation of
  // another boat, as the chat panel does.
  useEffect(() => {
    setConversationId(userId === null ? null : loadNeoConversationId('mate', { userId, boatId }));
  }, [boatId, userId]);
  // Alarm changes this turn (ADR 0210): what Neo changed, and the life-safety
  // changes waiting for the owner's click.
  const [alarmChanges, setAlarmChanges] = useState<NeoAlarmChange[]>([]);
  const [changeRequests, setChangeRequests] = useState<NeoAlarmChangeRequest[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  // Bumped per send: a response that arrives after the bar unmounted or after a
  // newer turn started must not write over the newer state.
  const generationRef = useRef(0);
  // The boat the bar is on now: a turn that ends after a boat change keeps no
  // conversation for the new boat.
  const boatIdRef = useRef(boatId);
  boatIdRef.current = boatId;

  // StrictMode mounts twice in development and a real unmount can happen
  // mid-stream — either way, drop the in-flight turn rather than leaving a
  // reader running against a gone component.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      generationRef.current += 1;
    };
  }, []);

  async function send(content: string): Promise<void> {
    if (sending || content.length === 0) return;
    setDraft('');
    setCommand(content);
    setReceipt(null);
    setAlarmChanges([]);
    setChangeRequests([]);
    setSending(true);

    let state = startMateTurn();
    setTurn(state);

    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const controller = new AbortController();
    abortRef.current = controller;
    let routed = false;
    let nextConversationId: string | null | undefined;
    const turnScope = resumeScope;
    const post = (resumeId: string | null) =>
      fetch('/v1/neo/chat/stream', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content }],
          boatId,
          ...(resumeId === null ? {} : { conversationId: resumeId }),
          // The busy label names a specialist at work (`agent` frames, TOM-543).
          capabilities: [NEO_AGENTS_CAPABILITY],
        }),
        credentials: 'same-origin',
        signal: controller.signal,
      });

    try {
      let res = await post(conversationId);
      if (generationRef.current !== generation) return;
      if (res.status === 404 && conversationId !== null) {
        // The server no longer knows the kept conversation. It refused before
        // the model was called, so forget it and ask in a new one (TOM-252).
        const body: unknown = await res.json().catch(() => null);
        if (isNeoConversationGone(conversationId, res.status, body)) {
          clearNeoConversationId('mate');
          setConversationId(null);
          res = await post(null);
          if (generationRef.current !== generation) return;
        }
      }
      if (res.status === 429) {
        // A Try it guest past a cap (TOM-868): the server's own words.
        const limit = demoLimitMessage(429, await res.json().catch(() => null));
        if (limit !== null) state = { ...state, limitMessage: limit };
      }
      if (!res.ok || res.body === null) throw new Error(`neo stream ${res.status}`);

      await readNeoStream(res.body, (event, data) => {
        // A read() that had already resolved still runs its continuation after
        // an abort, so the guard belongs HERE as well as at the await
        // boundaries (the same rule NeoChat follows).
        if (generationRef.current !== generation || controller.signal.aborted) return;
        if (event === 'done') nextConversationId = data.conversationId ?? undefined;
        if (event === 'mutations' && (data.mutations ?? []).length > 0) {
          // A dashboard command typed into this bar still applies — the
          // Instruments page listens for this and refetches. No UI here: this
          // bar is about alarms, and inventing a chip for it would be scope.
          window.dispatchEvent(new CustomEvent(DASHBOARD_UPDATED_EVENT));
        }
        // Account changes Neo made (ADR 0225 / 0226): no chip on this bar, but
        // new units re-render the page and an open Profile refetches its notes.
        if (event === 'preferences' && parsePreferenceChanges(data.changes).length > 0) {
          void auth.refresh();
        }
        if (event === 'memories' && parseMemoryChanges(data.changes).length > 0) {
          window.dispatchEvent(new CustomEvent(NEO_MEMORIES_UPDATED_EVENT));
        }
        // A pin Neo made (ADR 0239): no chip here either; an open board refetches.
        if (event === 'board' && parseBoardChanges(data.changes).length > 0) {
          window.dispatchEvent(new CustomEvent(NEO_BOARD_UPDATED_EVENT));
        }
        if (event === 'alarm_changes') {
          // Changes Neo MADE — reported whatever the reply says, and the page
          // refetches so the list shows them.
          const changes = parseAlarmChanges(data.changes, boatId);
          if (changes.length > 0) {
            setAlarmChanges((prev) => [...prev, ...changes]);
            onChange();
          }
        }
        if (event === 'alarm_change_requests') {
          // Life-safety changes: cards under the bar, like drafts.
          const requests = parseAlarmChangeRequests(data.requests, boatId);
          if (requests.length > 0) {
            setChangeRequests((prev) => [...prev, ...requests]);
            if (!routed) {
              routed = true;
              onProposals();
            }
          }
        }
        const before = state.proposals.length;
        // This bar is a BOAT PAGE surface: a proposal stamped with any other
        // boat is dropped at decode, before it can render or set `drafted`.
        state = foldMateEvent(state, event, data, boatId);
        if (!routed && state.proposals.length > before) {
          routed = true;
          onProposals();
        }
        setTurn(state);
      });
    } catch {
      // Every failure reads the same to the owner (lib/mateBar's one honest
      // line); the provider's own message is not theirs to decode. Drafts that
      // already arrived survive it.
      state = { ...state, errored: true };
    } finally {
      if (generationRef.current === generation) {
        setTurn(endMateTurn(state));
        setSending(false);
        if (
          nextConversationId !== undefined &&
          nextConversationId !== null &&
          boatIdRef.current === boatId
        ) {
          setConversationId(nextConversationId);
          saveNeoConversationId('mate', turnScope, nextConversationId);
        }
      }
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void send(draft.trim());
  }

  // Drop one card from the turn, BY IDENTITY. `drafted` stays true, so a turn
  // that failed AFTER producing this draft never falls back to the "couldn't
  // answer" line once the owner has dealt with it. Identity matters: an
  // in-flight confirm's completion must remove ITS card, not whichever card
  // holds its old position after a sibling was discarded.
  function dropProposal(clientId: string) {
    setTurn((prev) => (prev === null ? prev : removeMateProposal(prev, clientId)));
  }

  function onCreated(clientId: string, title: string, compileStatus: string | null) {
    setReceipt(mateReceiptMessage(title, compileStatus));
    // Only the confirmed draft goes. A turn that produced two drafts still owes
    // the owner a decision on the other one — clearing it here would throw away
    // a draft they never answered.
    dropProposal(clientId);
    onChange();
  }

  const reply = turn === null ? '' : mateReply(turn);
  const errorLine = turn === null ? null : mateErrorLine(turn);
  const proposals = turn?.proposals ?? [];

  return (
    <section className="al-mate" data-testid="alarms-mate-bar">
      <form className="al-mate__bar" onSubmit={onSubmit}>
        <span className="al-mate-avatar" aria-hidden="true">
          <MateAlarmIcon />
        </span>
        <label className="sr-only" htmlFor="alarms-mate-input">
          Tell the boat what to watch
        </label>
        <input
          id="alarms-mate-input"
          className="al-mate__input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={PLACEHOLDER}
          autoComplete="off"
          data-testid="alarms-mate-input"
        />
        {/* The bar submits on Enter, as the design says. This keeps that
            reachable without a pointer-free dead end; it is not a second
            affordance on screen. */}
        <button type="submit" className="sr-only" disabled={sending || draft.trim().length === 0}>
          Ask Neo
        </button>
      </form>

      {sending && turn !== null ? (
        <p className="al-mate__status" role="status">
          {mateBusyLabel(turn)}
        </p>
      ) : null}

      {reply.length > 0 ? (
        <p className="al-mate__reply" title={turn?.text} data-testid="alarms-mate-reply">
          {reply}
        </p>
      ) : null}

      {errorLine !== null ? (
        <p className="al-mate__error" role="alert" data-testid="alarms-mate-error">
          {errorLine}
        </p>
      ) : null}

      {receipt !== null ? (
        <p
          className={`al-mate-receipt${receipt.needsAttention ? ' al-mate-receipt--attention' : ''}`}
          role="status"
          data-testid="alarms-mate-receipt"
        >
          <CheckCircleFillIcon />
          {receipt.message}
        </p>
      ) : null}

      {alarmChanges.map((change, i) => (
        <p
          key={`${change.alarmId}-${i}`}
          className="al-mate-receipt"
          role="status"
          data-testid="alarms-mate-alarm-change"
        >
          <CheckCircleFillIcon />
          {alarmChangeChip(change).replace(/^✓ /, '')}
        </p>
      ))}

      {changeRequests.map((request) => (
        <NeoAlarmChangeCard
          key={request.clientId}
          request={request}
          conversationId={() => conversationIdRef.current}
        />
      ))}

      {proposals.map((proposal) => (
        <MateProposalCard
          key={proposal.clientId}
          proposal={proposal}
          command={command}
          onCreated={(title, compileStatus) => onCreated(proposal.clientId, title, compileStatus)}
          onDiscard={() => dropProposal(proposal.clientId)}
        />
      ))}
    </section>
  );
}
